import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Inbox, RefreshCw, AlertTriangle, CheckCircle2, Settings2, List, Building2, Tags, ShieldCheck } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/SimpleToast";
import { EmailList } from "@/components/mailbox/EmailList";
import { MailboxRules } from "@/components/mailbox/MailboxRules";
import { ScrollFade } from "@/components/ScrollFade";
import { TopicsManager } from "@/components/mailbox/TopicsManager";
import { useMailboxBadge } from "@/components/mailbox/useMailboxBadge";

const VIEWS = [["list", "Liste", List], ["target", "Nach Kunde/Hersteller", Building2], ["topic", "Nach Thema", Tags]];
const EMPTY = {
  pending: "Alles bearbeitet – keine offenen Mails im Posteingang.",
  open: "Alles zugeordnet – keine offenen E-Mails.",
  assigned: "Noch keine zugeordneten E-Mails.",
  ignored: "Keine ignorierten E-Mails.",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "noch nie");

const Count = ({ n, tone = "slate" }) => (
  <span className={`ml-1.5 min-w-[22px] px-1.5 py-0.5 rounded-full text-[11px] font-semibold tabular-nums ${tone === "orange" ? "bg-[#E8700A] text-white" : tone === "amber" ? "bg-amber-100 text-amber-800" : "bg-slate-200/80 text-slate-700"}`}>{n ?? "…"}</span>
);

function StatusPill({ status }) {
  if (!status) return null;
  const err = !!status.last_error;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 ${err ? "border-amber-300 bg-amber-50 text-amber-800" : "bg-card text-muted-foreground"}`}
      title={err ? `${status.last_error} (letzter Versuch ${fmt(status.last_attempt_at)})` : undefined} data-testid="mailbox-status">
      {err ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
      {err ? status.last_error : `Abgerufen ${fmt(status.last_sync_at)}`}
      <span className="hidden sm:inline text-slate-400">· {status.mode === "graph" ? "Microsoft Graph" : "EWS"}</span>
    </span>
  );
}

export default function Mailbox() {
  const { user } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("pending");
  const badge = useMailboxBadge();
  const [listKey, setListKey] = useState(0);
  const [view, setView] = useState(() => localStorage.getItem("mailbox-view") || "list");
  const [topicsOpen, setTopicsOpen] = useState(false);

  const loadStatus = useCallback(() => mailboxApi.status().then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { loadStatus(); }, [loadStatus, listKey]);

  const syncNow = async () => {
    await mailboxApi.syncNow();
    toast.success("Abruf gestartet – neue Mails erscheinen in Kürze");
    setTimeout(() => { loadStatus(); setListKey((k) => k + 1); }, 8000);
  };

  const c = status?.counts || {};
  const showViews = tab !== "ignored" && tab !== "rules";
  const changeView = (v) => { setView(v); localStorage.setItem("mailbox-view", v); };
  return (
    <div className="space-y-5 animate-fade-in" data-testid="mailbox-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3"><Inbox className="w-7 h-7 text-[#1E3A5F]" />Service-Postfach</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5" title="Gespeichert werden nur Absender, Empfänger, Betreff, Datum und ein kurzer Textauszug – keine Anhänge.">
            {status?.mailbox || "service@r-network.de"} · Abruf alle 5 Minuten<ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={status} />
          {user?.role === "admin" && <Button variant="outline" size="sm" onClick={syncNow} data-testid="mailbox-sync-btn"><RefreshCw className="w-4 h-4 mr-2" />Jetzt abrufen</Button>}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ScrollFade>
            <TabsList>
              <TabsTrigger value="pending" data-testid="mailbox-tab-pending">Unbearbeitet<Count n={badge.pending} tone="orange" /></TabsTrigger>
              <TabsTrigger value="open" data-testid="mailbox-tab-open">Zuordnung offen<Count n={c.open} tone="amber" /></TabsTrigger>
              <TabsTrigger value="assigned" data-testid="mailbox-tab-assigned">Zugeordnet<Count n={c.assigned} /></TabsTrigger>
              <TabsTrigger value="ignored" data-testid="mailbox-tab-ignored">Ignoriert<Count n={c.ignored} /></TabsTrigger>
              <TabsTrigger value="rules" data-testid="mailbox-tab-rules">Regeln</TabsTrigger>
            </TabsList>
          </ScrollFade>
          {showViews && (
            <div className="flex items-center gap-2">
              {view === "topic" && user?.role === "admin" && (
                <Button variant="ghost" size="sm" onClick={() => setTopicsOpen(true)} data-testid="mailbox-topics-manage"><Settings2 className="w-4 h-4 mr-1.5" />Themen verwalten</Button>
              )}
              <div className="inline-flex rounded-lg border p-0.5 bg-muted/40" role="group" data-testid="mailbox-view-toggle">
                {VIEWS.map(([v, label, Icon]) => (
                  <button key={v} onClick={() => changeView(v)} title={label}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md transition-colors ${view === v ? "bg-background shadow-sm font-medium text-[#1E3A5F]" : "text-muted-foreground hover:text-foreground"}`} data-testid={`mailbox-view-${v}`}>
                    <Icon className="w-4 h-4" /><span className="hidden md:inline">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {topicsOpen && <TopicsManager open={topicsOpen} onOpenChange={setTopicsOpen} onChanged={() => setTimeout(() => setListKey((k) => k + 1), 4000)} />}
        <TabsContent value="rules">{tab === "rules" && <MailboxRules />}</TabsContent>
        {Object.keys(EMPTY).map((s) => (
          <TabsContent key={s} value={s} className="mt-0">
            {tab === s && (
              <EmailList key={`${s}-${listKey}`} split params={s === "pending" ? { work: "pending" } : { status: s }} showTarget testId={`mailbox-list-${s}`}
                onLoaded={loadStatus} view={s === "ignored" ? "list" : view} emptyText={EMPTY[s]} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
